// src/components/Documentation.jsx
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  FaChartLine,
  FaExchangeAlt,
  FaDollarSign,
  FaBuilding,
  FaMoneyBillWave
} from 'react-icons/fa';

// Imports des fichiers Markdown
import DataOrganizationRaw from '../documentation/data-organization.md?raw';

const sections = [
  {
    title: "📊 Organisation de vos données",
    content: DataOrganizationRaw,
  },
];

function Documentation({ theme, toggleTheme }) {
  const navigate = useNavigate();

  useEffect(() => {
      window.scrollTo(0, 0);          // ← ça force le scroll en haut
    }, []);

  // === ARRAY DES TEMPLATES avec react-icons ===
  const csvTemplates = [
    {
      title: "Portefeuille",
      description: "Valorisation des positions une fois par mois",
      icon: FaChartLine,
      color: "blue",
      href: "/templates/template-portfolio.csv",
      filename: "template-portfolio.csv",
    },
    {
      title: "Entreprises",
      description: "Listes des sociétés détenues en portefeuille",
      icon: FaBuilding,
      color: "purple",
      href: "/templates/template-companies.csv",
      filename: "template-companies.csv",
    },
    {
      title: "Transactions", 
      description: "Liste des achats et ventes",
      icon: FaExchangeAlt,
      color: "indigo",
      href: "/templates/template-transactions.csv",
      filename: "template-transactions.csv",
    },
    {
      title: "Dividendes",
      description: "Liste des dividendes perçues",
      icon: FaDollarSign,
      color: "green",
      href: "/templates/template-dividends.csv",
      filename: "template-dividends.csv",
    },
    {
      title: "Cash",
      description: "Valorisation de la liquidité à la fin de chaque mois",
      icon: FaMoneyBillWave,
      color: "emerald",
      href: "/templates/template-cash.csv",
      filename: "template-cash.csv",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <header className="flex justify-between items-center mb-12">
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-2"
          >
            ← Retour
          </button>
          <button
            onClick={toggleTheme}
            className="px-5 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-md"
          >
            {theme === 'light' ? 'Mode Sombre' : 'Mode Clair'}
          </button>
        </header>

        {/* Titre */}
        <h1 className="text-4xl font-bold text-center mb-4 text-gray-900 dark:text-white">
          📚 Guide d’utilisation & Organisation des données
        </h1>
        <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12">
          Comprenez facilement comment vos données sont organisées et utilisées.
        </p>

        {/* Sections */}
        <div className="space-y-12 mb-6">  {/* au lieu de space-y-16 */}
          {/* Section 1 : Les onglets */}
          <section>
            <h2 className="text-3xl font-semibold mb-6">📊 Les différents onglets</h2>
            <ul className="list-disc pl-6 space-y-4 text-base">
              <li><strong>Portefeuille</strong> : Valorisation de vos positions une fois par mois.</li>
              <li><strong>Transactions</strong> : Historique complet de vos achats et ventes.</li>
              <li><strong>Dividendes</strong> : Historique complet des dividendes perçues.</li>
              <li><strong>Cash</strong> : Valorisation de vos liquidité à la fin de chaque mois.</li>
              <li><strong>Entreprises</strong> : Listes des sociétés détenues en portefeuille</li>
              <li><strong>Metrics</strong> : Analyses avancées, comparaisons avec benchmarks, DCA, etc.</li>
              <li><strong>Monthly Update</strong> : Outils pour la mise à jour mensuelle des données.</li>
            </ul>
          </section>
          {sections.map((section, index) => (
            <section key={index}>
              <h2 className="text-3xl font-semibold mb-4 text-gray-900 dark:text-white">  {/* mb-4 au lieu de mb-8 */}
                {section.title}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 my-4">
                Voici comment vos informations sont structurées dans l'application. 
                La colonne **Nom du champ** indique le nom exact utilisé dans la base de données — 
                utile si vous préparez un fichier CSV pour importer vos données.
              </p>
              <div className="prose prose-base dark:prose-invert max-w-none">  {/* prose-base au lieu de prose-lg */}
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Tableau compact avec bordures fines
                    table: ({ children }) => (
                      <div className="my-6 overflow-x-auto">
                        <table className="min-w-full border border-gray-200 dark:border-gray-700 rounded-md">
                          {children}
                        </table>
                      </div>
                    ),
                    thead: ({ children }) => (
                      <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                        {children}
                      </thead>
                    ),
                    th: ({ children }) => (
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        {children}
                      </th>
                    ),
                    tbody: ({ children }) => (
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {children}
                      </tbody>
                    ),
                    tr: ({ children }) => (
                      <tr>
                        {children}
                      </tr>
                    ),
                    td: ({ children }) => (
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {children}
                      </td>
                    ),
                  }}
                >
                  {section.content}
                </ReactMarkdown>
              </div>
            </section>
          ))}

          {/* === NOUVELLE VERSION AMÉLIORÉE DES MODÈLES CSV === */}
          <section className="mt-12">
            <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
              📄 Modèles CSV pour importer vos données
            </h2>

            <p className="text-base text-gray-600 dark:text-gray-300 mb-10 max-w-3xl">
              Téléchargez ces modèles, remplissez-les dans Excel ou Google Sheets, puis importez-les
              facilement dans votre portefeuille.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {csvTemplates.map((template) => {
                const colorClasses = {
                  blue: "bg-blue-50 hover:bg-blue-100 border-blue-200 dark:bg-blue-950/30 dark:hover:bg-blue-900/40 dark:border-blue-800/60",
                  purple: "bg-purple-50 hover:bg-purple-100 border-purple-200 dark:bg-purple-950/30 dark:hover:bg-purple-900/40 dark:border-purple-800/60",
                  indigo: "bg-indigo-50 hover:bg-indigo-100 border-indigo-200 dark:bg-indigo-950/30 dark:hover:bg-indigo-900/40 dark:border-indigo-800/60",
                  green: "bg-green-50 hover:bg-green-100 border-green-200 dark:bg-green-950/30 dark:hover:bg-green-900/40 dark:border-green-800/60",
                  emerald: "bg-emerald-50 hover:bg-emerald-100 border-emerald-200 dark:bg-emerald-950/30 dark:hover:bg-emerald-900/40 dark:border-emerald-800/60",
                };

                return (
                  <a
                    key={template.title}
                    href={template.href}
                    download={template.filename}
                    className={`
                      group relative flex flex-col items-center p-6 rounded-xl border transition-all duration-300
                      ${colorClasses[template.color] ?? 'bg-gray-50 hover:bg-gray-100 border-gray-200'}
                      hover:shadow-lg hover:-translate-y-1
                    `}
                  >
                    {/* ICÔNE react-icons (même syntaxe que lucide !) */}
                    <div className="mb-5 rounded-full bg-white/80 dark:bg-slate-800 p-4 shadow-sm transition-transform group-hover:scale-110">
                      <template.icon className="h-10 w-10 text-gray-700 dark:text-gray-200" />
                    </div>

                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2 text-center">
                      {template.title}
                    </h3>

                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-4 min-h-[3rem]">
                      {template.description}
                    </p>

                    <span className="text-xs font-medium px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                      Modèle CSV
                    </span>
                  </a>
                );
              })}
            </div>
          </section>
        </div>

        <div className="text-center mt-16 text-gray-500 dark:text-gray-400">
          Documentation mise à jour régulièrement.
        </div>
      </div>
    </div>
  );
}

export default Documentation;